import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  SelectChangeEvent,
  Grid,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { getValidatedFile, postStarEmission, postUploadLayout, putUploadLayout } from "services/certifications.service";
import { useAuthMfaSecure } from "providers/AuthSecureManage/AuthMfaSecureManage";

interface Props {
  issuanceStatus: (id: number) => void;
  getFileName: (name: string) => void;
  typeEmission: string;
}

export function ValidateFile(props: Props) {
  const { issuanceStatus, getFileName, typeEmission } = props;
  const { listModules } = useAuthMfaSecure();

  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [allies, setAllies] = useState<boolean>(false);

  const [isLoadingValidation, setIsLoadingValidation] = useState(false);
  const [validatedFile, setValidatedFile] = useState<any>();
  const [successfullyValidatedFile, setSuccessfullyValidatedFile] = useState<boolean>(false);
  const [pollingCount, setPollingCount] = useState(0);

  const [respPostUploadLayout, setRespPostUploadLayout] = useState<any>();
  const [startEmission, setStartEmission] = useState<boolean>(false);

  const [isDisabledRestart, setIsDisabledRestart] = useState(true);
  const [fileName, setFileName] = useState<string>();

  const [userModules, setUserModules] = useState<any[]>([]);
  const [partnerName, setPartnerName] = useState<string>("");
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch user modules from Supabase
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const modules = await listModules();
        setUserModules(modules || []);
        
        // Obtener el nombre del partner del primer módulo
        if (modules && modules.length > 0 && modules[0].partner) {
          const pName = modules[0].partner.name.toLowerCase();
          setPartnerName(pName);
        }
      } catch (error) {
        console.error("ValidateFile: Error fetching modules", error);
      }
    };
    fetchModules();
  }, [listModules]);

  const partnersIds = {
    rappi: {
      certification: 2,
      fc3: 1,
    },
    zubale: {
      certification: 7
    },
  }
  // Obtener partnerId numérico del primer módulo (todos los módulos del usuario tienen el mismo partner)
  // El backend espera IDs numéricos (2=Rappi, 7=Zubale, 10=Scape)
  // Para Zubale, forzamos el partnerId a 7 en lugar del numeric_id original
  let partnerId = partnersIds?.[partnerName]?.[typeEmission];
  
  if (!partnerId) {
    partnerId = userModules.length > 0 ? userModules[0]?.partner?.numeric_id : undefined;
  }

  // Determinar opciones de menú basado en el nombre del partner
  const isRappi = partnerName === 'rappi';
  const isZubale = partnerName === 'zubale';
  const isScape = partnerName === 'scape';


  const resetState = () => {
    window.location.reload();
  };

  const minLength = 3;


  const post_uploadLayout = useCallback(async (file: any, alliesValue: boolean) => {
    if (!partnerId) {
      console.error("ValidateFile: No partnerId available");
      alert("Error: No se pudo obtener el partner. Por favor recarga la página.");
      return;
    }
    
    try {
      const module_data = { partnerId, type: typeEmission };
      const resp = await postUploadLayout(typeEmission, fileName, module_data, alliesValue);
      
      // La API devuelve los datos directamente en resp.data, no en resp.data.resource
      const responseData = resp.data.resource || resp.data;
      
      setRespPostUploadLayout(responseData);
      put_uploadLayout(file, responseData.business_event_id, responseData.upload_url);
    } catch (error) {
      console.error("ValidateFile: Error in post_uploadLayout", error);
    } finally {
      setIsDisabled(true);
      setIsLoading(false);
      setIsDisabledRestart(false);
    }
  }, [fileName, typeEmission, partnerId]);

  const put_uploadLayout = useCallback(async (file: any, business_event_id: number, upload_url: string) => {
    if (!file) {
      return;
    }
    file;
    try {
      const resp = await putUploadLayout(upload_url, file);
      // Activar el estado de validación antes de iniciar el polling
      setIsLoadingValidation(true);
      pullingValidatedHandler(business_event_id);
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      setIsLoadingValidation(false);
    }
  }, []);

  

  const pullingValidatedHandler = useCallback(async (business_event_id: number, attemptCount: number = 0) => {
    const MAX_ATTEMPTS = 40; // 40 intentos * 15s = 10 minutos máximo
    
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    
    try {
      const response = await getValidatedFile(business_event_id);
      
      // Manejar diferentes estructuras de respuesta
      const validationData = response.data?.resource || response.data;
      
      if (!validationData) {
        console.error('No validation data in response');
        throw new Error('Invalid response structure');
      }
      
      setPollingCount(attemptCount + 1);
      
      // Validación exitosa
      if (validationData.validations_ended && validationData.header_valid) {
        setIsLoadingValidation(false);
        setSuccessfullyValidatedFile(true);
        setPollingCount(0);
      }
      // Validación terminada pero con errores
      else if (validationData.validations_ended && !validationData.header_valid) {
        setIsDisabled(false);
        setIsLoadingValidation(false);
        setFileUploaded(false);
        setValidatedFile(validationData);
        setPollingCount(0);
      }
      // Validación aún en proceso
      else {
        if (attemptCount >= MAX_ATTEMPTS) {
          setIsLoadingValidation(false);
          setIsDisabled(false);
          setPollingCount(0);
          return;
        }
        
        pollingTimeoutRef.current = setTimeout(() => {
          pullingValidatedHandler(business_event_id, attemptCount + 1);
        }, 15000);
      }
    } catch (error) {
      console.error('Error polling validation:', error);
      setIsLoadingValidation(false);
      setIsDisabled(false);
      setPollingCount(0);
      alert('Error al validar el archivo. Por favor intenta de nuevo.');
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const post_StartEmission = useCallback(async () => {
    try {
      await postStarEmission(
        respPostUploadLayout.business_event_id,
        typeEmission
      );
      issuanceStatus(respPostUploadLayout.business_event_id);
      setStartEmission(true);
    } catch (error) {
      console.error('Error starting emission:', error);
      alert('Error al iniciar la emisión. Por favor intenta de nuevo.');
    }
  }, [respPostUploadLayout, typeEmission, issuanceStatus]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    if (!file) return;
    setFileName(file.name);
    getFileName(file.name);
    setFileUploaded(true);
  };

  const handleActionChange = (event: SelectChangeEvent<string>) => {
    setSelectedAction(event.target.value);
    setAllies(event.target.value === "allies");
  };

  const uploadFile = () => {
    post_StartEmission();
  };

  const onFinish = async (event: React.FormEvent<EventTarget>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const file = event.target[0].files[0];
      
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        await post_uploadLayout(text, allies);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error("ValidateFile: Error in onFinish", error);
    }
  };

  useEffect(() => {
    setSelectedAction("")
  }, [typeEmission])

  return (
    <>
      <Typography variant="h5" component="h2" style={{ fontSize: "1.1rem", display: "flex" }}>
        Selección de archivo {isDisabled && <CheckCircleOutlineIcon style={{ color: "#6BA233", marginLeft: "1rem" }} />}
      </Typography>

      <FormControl component="form" onSubmit={onFinish} autoComplete="off" sx={{ ...Styles.formContainer }}>
        <input
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
          id="contained-button-file"
          disabled={isDisabled}
        />
        <label htmlFor="contained-button-file">
          <Button
            variant="outlined"
            component="span"
            sx={{
              ...fileUploaded ? Styles.buttonUploadSuccess : Styles.buttonUpload,
              whiteSpace: 'nowrap'
            }}
            endIcon={fileUploaded ? <CheckCircleOutlineIcon /> : <UploadOutlined />}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {fileUploaded ? `Archivo ${fileName} cargado` : "Seleccionar archivo (.csv)"}
            </span>
          </Button>
        </label>

        <FormControl style={{ minWidth: "20rem" }}>
          <InputLabel sx={{ marginTop: "-0.5rem" }}>Seleccionar una acción a realizar</InputLabel>
          <Select
            sx={{ height: "2.5rem" }}
            name="select"
            label="Seleccionar una acción a realizar"
            value={selectedAction}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedAction(value);
              // allies = true cuando se selecciona "allies"
              setAllies(value === "allies");
            }}
          >
            {/* Rappi - Certification */}
            {isRappi && typeEmission === 'certification' && [
              <MenuItem key="rts" value="rts">Emisión a RTs</MenuItem>,
              <MenuItem key="allies" value="allies">Emisión a Aliados</MenuItem>
            ]}
            
            {/* Rappi - FC3 (Factura cuenta a tercero) */}
            {isRappi && typeEmission === 'fc3' &&
              <MenuItem value="rts">Emisión</MenuItem>
            }
            
            {/* Zubale - Certification */}
            {isZubale && typeEmission === 'certification' &&
              <MenuItem value="rts">Emisión</MenuItem>
            }
            
            {/* Zubale - Common Invoices (Factura normal masiva) */}
            {isZubale && typeEmission === 'common_invoices' &&
              <MenuItem value="rts">Emisión</MenuItem>
            }
            
            {/* Scape - Common Invoices (Factura normal masiva) */}
            {isScape && typeEmission === 'common_invoices' &&
              <MenuItem value="rts">Emisión</MenuItem>
            }
            
            {/* Default para otros casos */}
            {!isRappi && !isZubale && !isScape &&
              <MenuItem value="rts2">Emisión</MenuItem>
            }
          </Select>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          sx={Styles.buttonForm}
          disabled={isDisabled || !fileUploaded || !selectedAction}
        >
          {isLoading ? <CircularProgress size={24} style={{ color: "white" }} /> : "Validar archivo"}
        </Button>
        <Button variant="contained" sx={Styles.buttonForm} onClick={resetState} disabled={isDisabledRestart}>
          Reiniciar proceso
        </Button>
      </FormControl>

      <Typography variant="h5" component="h2" style={{ fontSize: "1.1rem", marginBottom: "1rem", display: "flex" }}>
        Validación de archivo{" "}
        {successfullyValidatedFile && <CheckCircleOutlineIcon style={{ color: "#6BA233", marginLeft: "1rem" }} />}
      </Typography>
      {(successfullyValidatedFile || validatedFile) && (
        <p style={Styles.textList}>
          La validación confirma que los campos del archivo coincidan con el tipo de valor del campo. Ten encuenta los
          criterios de evaluación de cada campo.
        </p>
      )}
      <div
        style={
          (successfullyValidatedFile || validatedFile) && !isLoadingValidation
            ? Styles.containerValidateFile
            : Styles.containerUpload
        }
      >
        {successfullyValidatedFile ? (
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex" }}>
              <CheckCircleOutlineIcon style={{ color: "#6BA233", marginRight: "0.5rem", fontSize: "38px" }} />
              <p style={Styles.textList}>
                ¡El archivo está correcto!
                <br />
                Confirma que es el archivo que quieres cargar
              </p>
            </div>
            {startEmission ? (
              <Typography variant="h5" component="h2" sx={Styles.textUpload}>
                Emisión iniciada
              </Typography>
            ) : (
              <Button variant="contained" sx={Styles.buttonForm} onClick={uploadFile}>
                Iniciar emisión
              </Button>
            )}
          </div>
        ) : isLoadingValidation ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CircularProgress
              style={{ marginBottom: "1rem", width: "80px", height: "80px", opacity: 0.5 }}
              thickness={4}
            />
            <Typography variant="h5" component="h2" sx={Styles.textUpload}>
              Estamos validando el archivo
            </Typography>
            {pollingCount > 0 && (
              <Typography variant="body2" sx={{ color: "#B3B3B3", marginTop: "0.5rem" }}>
                Intento {pollingCount} - Esto puede tomar unos minutos...
              </Typography>
            )}
          </div>
        ) : validatedFile ? (
          
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ display: "flex" }}>
                 <ExclamationCircleOutlined style={{ color: "#DA1E28", marginRight: "0.5rem", fontSize: "38px" }} />
                 <p style={Styles.textList}>
                   El archivo tiene errores en formato.
                   <br />
                   Corrige los errores para cada campo y valídalo de nuevo.
                 </p>
            </div>
          </div>
        ) : (
          <Typography variant="h5" component="h2" sx={Styles.textUpload}>
            Cargar archivo CSV
          </Typography>
        )}
      </div>
      <Grid container sx={Styles.containerDescription}>
        <Grid xs={typeEmission==='certification' ? 5.9 : 5.5} sx={Styles.columnDescription}>
          <Typography sx={Styles.containerTitle}># Numero</Typography>
          <Grid container>
            <Grid xs={4}>
              {typeEmission==='certification' ? (
                <>
                  <li style={Styles.textList}>Monto sin propina</li>
                  <li style={Styles.textList}>Propinas</li>
                  <li style={Styles.textList}>Total del servicio</li>
                </>
              ) : (
                <>
                  <li style={Styles.textList}>ID Contrato</li>
                  <li style={Styles.textList}>SKID</li>
                  <li style={Styles.textList}>Régimen</li>
                </>
              )}
            </Grid>
            <Grid xs={4}>
              {typeEmission==='certification' ? (
                <>
                  <li style={Styles.textList}>IVA trasladado</li>
                  <li style={Styles.textList}>IVA retenido</li>
                  <li style={Styles.textList}>ISR retenido</li>
                </>
              ) : (
                <>
                  <li style={Styles.textList}>MU</li>
                  <li style={Styles.textList}>Forma pago</li>
                  <li style={Styles.textList}>Uso CFDI</li>
                </>
              )}
            </Grid>
            <Grid xs={4}>
              {typeEmission==='certification' ? (
                <>
                  <li style={Styles.textList}>Monto total retenido</li>
                </>
              ) : (
                <>
                  <li style={Styles.textList}>Código producto</li>
                  <li style={Styles.textList}>Monto factura</li>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={typeEmission==='certification' ? 1.6 : 4} sx={Styles.columnDescription}>
          <Typography sx={Styles.containerTitle}>"a" Texto</Typography>
          {typeEmission==='certification' ? (
            <>
              <li style={Styles.textList}>RFC</li>
              <li style={Styles.textList}>Nombre</li>
              <li style={Styles.textList}>Código postal</li>
            </>
          ) : (
            <Grid container>
              <Grid xs={6}>
                <li style={Styles.textList}>RFC</li>
                <li style={Styles.textList}>Nombre</li>
                <li style={Styles.textList}>Código postal</li>
              </Grid>
              <Grid xs={6}>
                <li style={Styles.textList}>Nombre receptor</li>
                <li style={Styles.textList}>RFC receptor</li>
                <li style={Styles.textList}>Descripción factura</li>
              </Grid>
            </Grid>
          )}
        </Grid>
        {typeEmission==='certification' && (
          <Grid xs={2} sx={Styles.columnDescription}>
            <Typography sx={Styles.containerTitle}>"%" Porcentaje</Typography>

            <li style={Styles.textList}>Monto sin propina</li>
            <li style={Styles.textList}>Propinas</li>
            <li style={Styles.textList}>Total del servicio</li>
          </Grid>
        )}
        <Grid xs={2.5} sx={Styles.columnDescription}>
          <Typography sx={Styles.containerTitle}>"dd/mm/aaaa" Fecha</Typography>

          <li style={Styles.textList}>Fecha inicio y fecha fin</li>
        </Grid>
      </Grid>
    </>
  );
}

const Styles = {
  buttonUpload: {
    width: "20rem",
    padding: "0.5rem 1rem",
    height: "2.5rem",
    borderRadius: "0.25rem",
    border: "1px dashed #1790EC",
    color: "#1790EC",
    backgroundColor: "#F4F9FE",
    textTransform: "none !important",
  },
  buttonUploadSuccess: {
    width: "20rem",
    padding: "0.5rem 1rem",
    height: "2.5rem",
    borderRadius: "0.25rem",
    border: "1px solid #6BA233",
    color: "#6BA233",
    backgroundColor: "#F6F9F4",
    textTransform: "none !important",
  },
  formContainer: {
    margin: "1.5rem 0rem 1.5rem 0rem",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "1rem",
  },
  buttonForm: {
    width: "10rem",
    height: "2.5rem",
    borderRadius: "8px",
    backgroundColor: "#1790EC",
    color: "#fff",
    border: "none",
    textTransform: "none !important",
  },
  textUpload: {
    fontSize: "1.25rem",
    color: "#B3B3B3",
  },
  containerUpload: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "2rem 0rem 2rem 0rem",
  },
  containerValidateFile: {
    backgroundColor: "#fff",
    padding: "1.5rem",
    marginTop: "1.5rem",
    borderRadius: "0.5rem",
    boxShadow: "0px 1px 2px 0px rgba(25, 57, 91, 0.30), 0px 1px 3px 1px rgba(25, 57, 91, 0.15)",
  },
  containerDescription: {
    background: "#F9F9F9",
    boxShadow: "0px 1px 3px 1px rgba(25, 57, 91, 0.15)",
    borderRadius: 8,
    marginTop: "1rem",
    padding: "0.5rem",
    fontSize: 14,
    marginBottom: "1.5rem",
  },
  containerTitle: {
    background: "#E6E6E6",
    borderRadius: 8,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: "0.5rem 1rem 0.5rem 1rem",
    marginBottom: "0.5rem",
  },
  columnDescription: {
    padding: "0.5rem",
  },
  textList: {
    color: "#4D4D4D",
    fontSize: 14,
  },
  iconExclamation: {
    color: "#DA1E28",
    marginRight: "0.5rem",
    fontSize: "38px",
  },
  iconCheck: {
    color: "#6BA233",
    marginRight: "0.5rem",
    fontSize: "38px",
  },
};
